"use client";

import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import HeaderStrip from "./components/HeaderStrip";
import HeroSection from "./components/HeroSection";
import StatsSection from "./components/StatsSection";
import WhatWeReplace from "./components/WhatWeReplace";
import ReferOut from "./components/ReferOut";
import ProcessSection from "./components/ProcessSection";
import ScopeSection from "./components/ScopeSection";
import NeighborhoodCoverage from "./components/NeighborhoodCoverage";
import FaqSection from "@/components/feature/FaqSection";
import ServiceAreasGrid from "@/components/feature/ServiceAreasGrid";
import RelatedServicesBlockService from "@/components/feature/RelatedServicesBlockService";
import BottomCta from "./components/BottomCta";
import LeadFormSection from "@/components/feature/LeadFormSection";
import AboutServiceSection from "@/components/feature/AboutServiceSection";
import { injectMetaTags } from "@/lib/seoHelpers";

const neighborhoods = [
  "Brickell",
  "Aventura",
  "Miami Beach",
  "Coral Gables",
  "Coconut Grove",
  "Doral",
  "Kendall",
  "Pinecrest",
  "Bay Harbor Islands",
  "Key Biscayne",
  "South Miami",
  "North Miami Beach",
];

export default function GlassReplacementPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const glassAboutImg =
    "https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/2f0b12f4-37b3-4c61-a813-b347917ca686_windows-glass-replacement.jpg?v=c9eb79fab0a0e7f926e54a59ba4b88ef";

  const glassFaqs = [
    {
      question: t("glass.faq1Q") as string,
      answer: t("glass.faq1A") as string,
    },
    {
      question: t("glass.faq2Q") as string,
      answer: t("glass.faq2A") as string,
    },
    {
      question: t("glass.faq3Q") as string,
      answer: t("glass.faq3A") as string,
    },
  ];

  useEffect(() => {
    document.title = currentLang === "es"
      ? "Reemplazo de Vidrios Miami \u2014 Marcos No Impacto"
      : "Window Glass Replacement Miami \u2014 Non-Impact Frames";

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = currentLang === "es"
      ? "Especialistas en reemplazo de vidrios no impacto en Miami. Servicio el mismo d\u00EDa o siguiente en marcos residenciales existentes. Documentaci\u00F3n fotogr\u00E1fica para seguro incluida."
      : "Miami non-impact glass replacement specialists. Same-day or next-day service in existing residential frames. Photo documentation for insurance included.";

    const cleanupSeo = injectMetaTags({
      title: currentLang === "es" ? "Reemplazo de Vidrios Miami | Vidrios No Impacto Mismo D\u00EDa" : "Glass Replacement Miami | Single-Pane Window Glass Same-Day",
      description: currentLang === "es" ? "Especialistas en reemplazo de vidrios no impacto en Miami. Servicio el mismo d\u00EDa o siguiente en marcos residenciales existentes." : "Miami glass replacement for existing window frames. Non-impact glass same day. Impact jobs referred to licensed contractors.",
      url: "https://impact-window-repair-miami.com/glass-replacement/",
      image: "https://impact-window-repair-miami.com/og/og-glass.jpg",
      imageAlt: "Window glass replacement Miami non-impact",
      breadcrumbs: [
        { name: currentLang === "es" ? "Inicio" : "Home", item: "https://impact-window-repair-miami.com/" },
        { name: currentLang === "es" ? "Reemplazo de Vidrios" : "Glass Replacement", item: "https://impact-window-repair-miami.com/glass-replacement/" }
      ],
      type: "website",
    });

    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: "Window Glass Replacement",
          provider: {
            "@type": "LocalBusiness",
            name: "Windows and Doors Repair LLC",
            telephone: "+17863774452",
            email: "contact@impact-window-repair-miami.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "848 Brickell Ave, PH-5",
              addressLocality: "Miami",
              addressRegion: "FL",
              postalCode: "33131",
              addressCountry: "US",
            },
            geo: {
              "@type": "GeoCoordinates",
              "latitude": 25.7617,
              "longitude": -80.1918
            },
            openingHoursSpecification: [{
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              "opens": "07:00",
              "closes": "21:00"
            }],
            priceRange: "$$",
            paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
            currenciesAccepted: "USD",
            areaServed: [
              {"@type": "City", "name": "Miami"},
              {"@type": "AdministrativeArea", "name": "Miami-Dade County"},
              "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach"
            ],
            knowsLanguage: ["en", "es"],
            knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
            slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
            inLanguage: currentLang === "es" ? "es-US" : "en-US"
          },
          areaServed: neighborhoods.map((n) => ({
            "@type": "City",
            name: n + ", Miami-Dade, FL",
          })),
          serviceType: "Window Glass Replacement",
          description: "Non-impact glass replacement in existing residential window frames across Miami-Dade.",
        },
        {
          "@type": "LocalBusiness",
          name: "Windows and Doors Repair LLC",
          telephone: "+17863774452",
          email: "contact@impact-window-repair-miami.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "848 Brickell Ave, PH-5",
            addressLocality: "Miami",
            addressRegion: "FL",
            postalCode: "33131",
            addressCountry: "US",
          },
          geo: {
            "@type": "GeoCoordinates",
            "latitude": 25.7617,
            "longitude": -80.1918
          },
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "07:00",
            "closes": "21:00"
          }],
          priceRange: "$$",
          paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
          currenciesAccepted: "USD",
          areaServed: [
            {"@type": "City", "name": "Miami"},
            {"@type": "AdministrativeArea", "name": "Miami-Dade County"},
            "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove", "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne", "South Miami", "North Miami Beach"
          ],
          knowsLanguage: ["en", "es"],
          knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
          slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          url: window.location.href,
          inLanguage: currentLang === "es" ? "es-US" : "en-US"
        },
      ],
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: glassFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    const faqScript = document.createElement("script");
    faqScript.setAttribute("type", "application/ld+json");
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      document.head.removeChild(script);
      document.head.removeChild(faqScript);
    };
  }, [currentLang]);

  return (
    <main className="min-h-screen">
      <HeaderStrip />
      <HeroSection />
      <LeadFormSection defaultService="glass" />
      <AboutServiceSection
        badge={t("glass.eyebrow") as string}
        imageSrc={glassAboutImg}
        imageAlt="Miami glazier installing replacement glass in existing residential window frame"
        heading={t("glass.aboutHeading") as string}
        lead={t("glass.intro") as string}
        bullets={[
          t("glass.aboutBullet1") as string,
          t("glass.aboutBullet2") as string,
          t("glass.aboutBullet3") as string,
          t("glass.aboutBullet4") as string,
          t("glass.aboutBullet5") as string,
        ]}
        closing={t("glass.aboutClosing") as string}
      />
      <StatsSection />
      <WhatWeReplace />
      <ReferOut />
      <ProcessSection />
      <ScopeSection />
      <NeighborhoodCoverage />
      <FaqSection items={glassFaqs} heading={t("glass.faqH2") as string} />
      <ServiceAreasGrid serviceName="Glass Replacement" serviceSlug="glass-replacement" />
      <RelatedServicesBlockService
        links={[
          { label: t("glass.related1") as string, path: "/window-hardware-repair/" },
          { label: t("glass.related2") as string, path: "/weatherstripping-sealing/" },
          { label: t("glass.related3") as string, path: "/sliding-door-repair/" },
        ]}
      />
      <BottomCta />
    </main>
  );
}